import { useEffect, useSyncExternalStore } from 'react';

/**
 * Cuántos compositores de texto libre de WAPI están montados AHORA MISMO, en
 * TODO el proceso de React — no por pestaña del chat (RET-2283).
 *
 * Existe porque `WalletProvider` vive muy por encima de `ChatProvider`
 * (`Providers.component.tsx`: RET-1763 lo documenta ahí), así que el provider
 * no puede preguntarle a `selectedChat` si hay un chat WAPI abierto. Quien SÍ
 * lo sabe es `useComposerServiceSeal`, montado dentro del árbol del chat —
 * así que es él quien avisa, con `useRegisterWapiComposerPresence`, y el
 * provider sólo escucha, con `useAnyWapiComposerOpen`.
 *
 * Un CONTADOR y no un booleano compartido a mano: abrir varios chats WAPI a
 * la vez (secuencial u concurrente) no debe apagar la señal mientras
 * CUALQUIERA siga montado — el último en cerrar es quien la apaga.
 *
 * Vive fuera de `Wallet.context.ts` a propósito: `tWalletContext` es el
 * contrato que el provider PUBLICA hacia abajo, y esto es la señal que viaja
 * en la dirección CONTRARIA (de un consumidor hacia el provider). Mezclar las
 * dos en un solo tipo ataría la forma de una pregunta interna de
 * implementación a un contrato que la vara de RET-2283 ya congeló.
 */
let openWapiComposers = 0;
const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): boolean {
  return openWapiComposers > 0;
}

/** Siempre `false`: ni WalletProvider ni este módulo corren en el servidor. */
function getServerSnapshot(): boolean {
  return false;
}

/**
 * Llamado por `useComposerServiceSeal` con `isWapiChat`: suma mientras el
 * chat en pantalla sea de WAPI (sin importar si su `state` es `ACTIVE` — la
 * lectura es del WORKSPACE, no de si ESTE chat puede cotizar ahora mismo), y
 * resta al desmontar o al dejar de serlo. Deliberadamente ciego a la cohorte
 * de wallet: el provider es quien decide si ese ingrediente basta para leer,
 * para no duplicar esa regla aquí.
 */
export function useRegisterWapiComposerPresence(isWapiChat: boolean): void {
  useEffect(() => {
    if (!isWapiChat) return;

    openWapiComposers += 1;
    notify();

    return () => {
      openWapiComposers -= 1;
      notify();
    };
  }, [isWapiChat]);
}

/** Leído por `Wallet.provider.tsx`: ¿sigue vivo al menos un compositor WAPI? */
export function useAnyWapiComposerOpen(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
